// src/pages/Exams/generate/StyleProfileSummary.tsx
// The "Exam style profile" block under "Predict a national exam". The backend
// (api/intelligence/exam_generation/style_profile.py) returns an AI-written
// profile: { style_descriptors: string[], avg_question_length: number,
// common_formats: string[], difficulty_inferred: {easy, medium, hard} }.
// The shape is loose (model output), so every field is read defensively.
import { Chip } from '../../../school_panel/components/ui';
import { useStyleProfile } from './hooks/useStyleProfile';

const KNOWN = ['style_descriptors', 'avg_question_length', 'common_formats', 'difficulty_inferred'];
const LEVELS = ['easy', 'medium', 'hard'] as const;
/** The model often returns a dozen openings; a short list reads better. */
const MAX_OPENINGS = 4;

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(v => String(v ?? '').trim()).filter(Boolean);
}

/** "scenario-based" → "Scenario based". */
export function plainWords(tag: string): string {
  const words = tag.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "which of the following" → "Which of the following…" (no doubled ellipsis,
 *  none after a full question). */
export function opening(pattern: string): string {
  const text = pattern.replace(/(\.{3}|…)+\s*$/, '').trim();
  const capped = `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  return /[?!.]$/.test(capped) ? capped : `${capped}…`;
}

/** {easy: 30, medium: 50, hard: 20} → "30% easy · 50% medium · 20% hard". */
export function difficultyLine(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const parts = LEVELS.flatMap(level => {
    const n = Number((value as Record<string, unknown>)[level]);
    return Number.isFinite(n) ? [`${Math.round(n)}% ${level}`] : [];
  });
  return parts.length ? parts.join(' · ') : null;
}

function humanizeKey(key: string): string {
  return plainWords(key);
}

function plainValue(value: unknown): string {
  if (Array.isArray(value)) return strings(value).join(', ');
  if (value !== null && typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => `${plainWords(k).toLowerCase()} ${String(v)}`).join(', ');
  }
  return String(value);
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Stacked: the block sits in a half-width column, too narrow for a label gutter.
    <div>
      <dt className="text-xs font-semibold text-muted">{label}</dt>
      <dd className="mt-1 text-xs text-ink">{children}</dd>
    </div>
  );
}

export function StyleProfileSummary({ neId, subjectId }: { neId: number | null; subjectId: number | null }) {
  const { data, isLoading } = useStyleProfile(neId, subjectId);
  if (!neId || !subjectId) return null;
  if (isLoading) {
    return <p className="mt-2 text-xs text-muted">Loading exam style profile…</p>;
  }
  // Tolerate undefined data (still resolving in a test double, or a failed
  // request) by treating it the same as an empty profile.
  const profile = (data ?? {}) as Record<string, unknown>;
  const descriptors = strings(profile.style_descriptors);
  const formats = strings(profile.common_formats);
  const avgLength = Number(profile.avg_question_length);
  const difficulty = difficultyLine(profile.difficulty_inferred);
  const extras = Object.entries(profile).filter(([k, v]) =>
    !KNOWN.includes(k) && v !== null && v !== undefined && plainValue(v) !== '');
  // The server's no-data fallback carries a placeholder difficulty mix with no
  // descriptors, formats or length: that is "no data", not a real profile.
  const empty = descriptors.length === 0 && formats.length === 0 && !(avgLength > 0) && extras.length === 0;

  return (
    <div className="mt-3 rounded-[12px] border border-line bg-ground px-3 py-2.5">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Exam style profile</p>
      {empty ? (
        <p className="mt-1 text-xs text-muted-2">No style data for this exam.</p>
      ) : (
        <dl className="mt-2 space-y-2">
          {descriptors.length > 0 && (
            <Row label="Question style">
              <span className="flex flex-wrap gap-1.5">
                {descriptors.map(d => <Chip key={d} tone="accent">{plainWords(d)}</Chip>)}
              </span>
            </Row>
          )}
          {formats.length > 0 && (
            <Row label="Typical openings">
              <ul className="space-y-0.5">
                {formats.slice(0, MAX_OPENINGS).map(f => <li key={f}>“{opening(f)}”</li>)}
              </ul>
              {formats.length > MAX_OPENINGS && (
                <p className="mt-0.5 text-muted-2">+{formats.length - MAX_OPENINGS} more</p>
              )}
            </Row>
          )}
          {difficulty && <Row label="Difficulty mix">{difficulty}</Row>}
          {avgLength > 0 && <Row label="Question length">About {Math.round(avgLength)} words on average</Row>}
          {extras.map(([k, v]) => <Row key={k} label={humanizeKey(k)}>{plainValue(v)}</Row>)}
        </dl>
      )}
    </div>
  );
}
